
import React from 'react';
import { Mic, MicOff, Video, VideoOff, Share, PhoneOff, Volume2, VolumeX, Headphones, HeadphoneOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Slider } from '@/components/ui/slider';
import SystemAudioControls from './SystemAudioControls';

interface LiveSessionControlsProps {
  isMicActive: boolean;
  isVideoActive: boolean;
  isAudioActive: boolean;
  isScreenSharing: boolean;
  isRecording: boolean;
  onToggleMic: () => void;
  onToggleVideo: () => void;
  onToggleAudio: () => void;
  onShareScreen: () => void;
  onLeaveSession: () => void;
  onToggleRecording: () => void;
  onLeave?: () => void; // Optional prop for compatibility
  onSystemAudioStreamChange?: (stream: MediaStream | null) => void;
  micAudioLevel?: number;
  isMonitoring?: boolean;
  onToggleMonitoring?: () => void;
  monitorVolume?: number;
  onMonitorVolumeChange?: (volume: number) => void;
}

const LiveSessionControls: React.FC<LiveSessionControlsProps> = ({
  isMicActive,
  isVideoActive,
  isAudioActive,
  isScreenSharing,
  isRecording,
  onToggleMic,
  onToggleVideo,
  onToggleAudio,
  onShareScreen,
  onLeaveSession,
  onToggleRecording,
  onLeave,
  onSystemAudioStreamChange,
  micAudioLevel = 0,
  isMonitoring = false,
  onToggleMonitoring,
  monitorVolume = 0.3,
  onMonitorVolumeChange,
}) => {
  // Use onLeave if provided, otherwise fall back to onLeaveSession
  const handleLeave = () => {
    if (onLeave) {
      onLeave();
    } else {
      onLeaveSession();
    }
  };

  return (
    <div className="bg-black/60 backdrop-blur-md border-t border-white/10 p-3 rounded-md">
      <div className="flex items-center justify-center gap-2 flex-wrap">
        <Button
          variant={isMicActive ? "outline" : "destructive"}
          size="icon"
          className="h-10 w-10 rounded-full"
          onClick={onToggleMic}
        >
          {isMicActive ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
        </Button>
        
        {isMicActive && (
          <div className="flex items-center gap-2 min-w-[140px]">
            <span className="text-xs text-white/70">Mic</span>
            <Progress 
              value={micAudioLevel * 100} 
              className="h-2 flex-1"
              indicatorClassName="bg-gradient-to-r from-primary to-purple-500"
            />
          </div>
        )}
        
        <Button
          variant={isVideoActive ? "outline" : "destructive"}
          size="icon"
          className="h-10 w-10 rounded-full"
          onClick={onToggleVideo}
        >
          {isVideoActive ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
        </Button>
        
        <SystemAudioControls
          onStreamChange={onSystemAudioStreamChange}
        />
        
        <Button
          variant={isScreenSharing ? "destructive" : "outline"}
          size="icon"
          className="h-10 w-10 rounded-full"
          onClick={onShareScreen}
        >
          <Share className="h-5 w-5" />
        </Button>
        
        {onToggleMonitoring && (
          <Button
            variant={isMonitoring ? "default" : "outline"}
            size="icon"
            className="h-10 w-10 rounded-full"
            onClick={onToggleMonitoring}
            title="Monitor your audio (use headphones to prevent feedback)"
          >
            {isMonitoring ? <Headphones className="h-5 w-5" /> : <HeadphoneOff className="h-5 w-5" />}
          </Button>
        )}
        
        {isMonitoring && onMonitorVolumeChange && (
          <div className="flex items-center gap-2 min-w-[140px]">
            <Volume2 className="h-4 w-4 text-white/70" />
            <Slider 
              value={[monitorVolume * 100]}
              onValueChange={(val) => onMonitorVolumeChange(val[0] / 100)}
              max={50}
              step={1}
              className="h-2 flex-1"
            />
          </div>
        )}
        
        <Button
          variant="destructive"
          size="icon"
          className="h-10 w-10 rounded-full"
          onClick={handleLeave}
        >
          <PhoneOff className="h-5 w-5" />
        </Button>
      </div>
    </div>
  );
};

export default LiveSessionControls;
